import { useEffect } from 'react';

import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { InfoOutlined, ListOutlined } from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useAtom } from 'jotai';
import { useParams } from 'react-router';

import { GroupWork } from '../../../../common-components/elements/icon/svgs/GroupWork';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { usePreferences } from '../../../../react-query/usePreferences';
import { useGetImageWithSegmentations } from '../../../../react-query/useSegmentations';
import {
  blueBlack400,
  blueBlack630,
  blueBlack700,
  blueUltramarine050,
  blueUltramarine055,
  white,
} from '../../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import type { ImageObject } from '../../types';

import { showSegmentationAtom } from './atoms';

const ToggleGroup = styled(ToggleButtonGroup)`
  column-gap: 0.125em;
  padding: 0.125em 0.125em 0.125em 0.125em;
  border-radius: 0.25em;
`;

const ToggleButtonStyled = styled(ToggleButton)`
  border: 0;
  background-color: ${blueBlack700};
  border-radius: 0.25em;
  font-weight: 400;
  gap: 0.25em;
  height: 2.92em;
  white-space: nowrap;

  .label {
    display: inline-block;
    font-size: 0.875em;
    max-width: 8ch;
    white-space: wrap;
    text-align: left;
  }

  .MuiSvgIcon-root {
    padding-right: 0.25em;
    margin: 0;

    &.info-pv {
      position: absolute;
      padding-right: 0;
      top: 0.125em;
      right: 0.125em;
      width: 0.5em;
      height: 0.5em;
      fill: ${blueBlack400};
    }
  }

  &.Mui-selected {
    background-color: ${blueBlack400};
    border-radius: 0.25em;
    font-weight: 500;
    color: ${white};

    .info-pv {
      fill: ${white};
    }
  }
  &.Mui-selected:hover,
  &:hover {
    background-color: ${blueBlack630};
    color: ${white};

    .info-pv {
      fill: ${white};
    }
  }
  span {
    text-transform: capitalize;
    color: ${white};
  }
  &[disabled] {
    background-color: ${blueBlack700};
    opacity: 0.5;
  }

  @media (max-width: 1400px) {
    font-size: 1em;
  }
`;

const StyledListOutlinedIcon = styled(ListOutlined)`
  fill: ${blueUltramarine055};
  margin-right: 0.5em;
`;

export const SegmentationViewToggle = ({
  activeImage,
}: {
  activeImage?: ImageObject;
}) => {
  const { id: patientId } = useParams<{ id: string }>();
  const { t } = useLingui();

  const [showSegmentation, setShowSegmentation] = useAtom(showSegmentationAtom);

  const { data: preferences } = usePreferences();

  useEffect(() => {
    if (preferences?.showSegmentationView !== undefined) {
      setShowSegmentation(preferences.showSegmentationView);
    } else {
      setShowSegmentation(false);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies. Either include it or remove the dependency array. */
  }, []);

  const imageWithSegmentations = useGetImageWithSegmentations(
    activeImage?.imageId,
  );
  const { trackEvent } = useTrackEvent();

  const handleToggle = (value: 'patient-view' | 'findings' | null) => {
    if (!value) return; // User clicks on already selected button
    const isNewToggleValue = value === 'patient-view';
    trackEvent(mixpanelEvents.patientViewToggle, {
      patientViewToggle: isNewToggleValue,
      patientId,
    });
    setShowSegmentation(isNewToggleValue);
  };

  const patientViewTooltip = !imageWithSegmentations
    ? `${t`Segmentations not available for`} ${
        activeImage?.imageId ? t`this image` : t`these images`
      }`
    : t`Patient View is for patient education purposes only and not FDA-cleared for diagnostic purposes. Click Clinical View for diagnosis.`;

  return (
    <Tooltip
      title={patientViewTooltip}
      placement="top"
      
    >
      <ToggleGroup
        onChange={(_, value) => handleToggle(value)}
        value={showSegmentation ? 'patient-view' : 'findings'}
        exclusive
        className="sentry-unmask"
      >
        <ToggleButtonStyled value="patient-view">
          <InfoOutlined className="info-pv" />
          <GroupWork />
          <VideaTypography
            variant={showSegmentation ? 'labelSmallStrong' : 'labelSmall'}
            color={showSegmentation ? blueUltramarine055 : blueUltramarine050}
            className="label"
          >
            <Trans>Patient View</Trans>
          </VideaTypography>
        </ToggleButtonStyled>
        <ToggleButtonStyled value="findings" data-testid="clinical-view-toggle">
          <StyledListOutlinedIcon />
          <VideaTypography
            variant={!showSegmentation ? 'labelSmallStrong' : 'labelSmall'}
            color={!showSegmentation ? blueUltramarine055 : blueUltramarine050}
            className="label"
          >
            <Trans>Clinical View</Trans>
          </VideaTypography>
        </ToggleButtonStyled>
      </ToggleGroup>
    </Tooltip>
  );
};
